import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("Confirming your Stripe payment...");

  const checkStatus = useCallback(async () => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      setMessage("Missing Stripe session ID.");
      return;
    }

    try {
      const response = await axios.get(`${API}/api/payments/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        setStatus("paid");
        setMessage("Your Premium subscription is active.");
        await refreshUser();
        setTimeout(() => navigate("/"), 1800);
      } else {
        setStatus("checking");
        setMessage("Stripe is still confirming payment. This usually takes a few seconds.");
      }
    } catch (error) {
      setStatus("error");
      setMessage(error.response?.data?.detail || "Unable to confirm payment yet.");
    }
  }, [searchParams, refreshUser, navigate]);

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 2500);
    return () => clearInterval(interval);
  }, [checkStatus]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="glass rounded-2xl p-8 max-w-md w-full text-center border border-white/10" data-testid="stripe-payment-success-page">
        {status === "paid" ? (
          <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-4" />
        ) : status === "error" ? (
          <AlertTriangle className="w-14 h-14 text-amber-300 mx-auto mb-4" />
        ) : (
          <Loader2 className="w-14 h-14 text-[#00f2ea] mx-auto mb-4 animate-spin" />
        )}
        <h1 className="text-2xl font-black mb-3">Stripe Payment</h1>
        <p className="text-white/65 mb-6" data-testid="stripe-payment-status-message">{message}</p>
        <div className="flex gap-3 justify-center">
          <Link to="/" data-testid="stripe-success-dashboard-link"><Button className="bg-[#00f2ea] text-black hover:bg-[#00d4d4]">Dashboard</Button></Link>
          <Link to="/subscription" data-testid="stripe-success-subscription-link"><Button variant="outline" className="border-white/15 text-white hover:bg-white/10">Subscription</Button></Link>
        </div>
      </div>
    </div>
  );
}